import { BarChart3, Bell, ChevronRight, Columns3, ListTodo, MessageCircle, Settings, Tag } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { LogoMark } from "./Logo";
import { OrgSwitcher } from "./OrgSwitcher";
import { mutedTextClass } from "./ui";
import { ProjectIconTile } from "@/features/project-settings";
import { v2Api } from "@/features/issue";
import { projectRef } from "@/features/issue/useWorkspace";
import { NewProjectButton } from "@/features/project";
import { useChatUnreadTotal } from "@/features/chat";
import { useUnreadNotificationCount } from "@/features/notifications";
import { cn } from "@/lib/cn";
import { isPaidPlan } from "@/lib/permissions";
import { useProjects } from "@/lib/queries";
import { useWorkspace } from "@/lib/workspace";
import type { MembershipSummary } from "@/lib/types";

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition",
    isActive
      ? "bg-brand-50 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-200"
      : "text-ink-600 hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100",
  );

// Board and Backlog hang off each project row: they are the two screens people
// live in, and without them the feature is only reachable by typing a URL.
const subLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    // 24px tall with an icon, not a 16px text link. These were three bare words
    // at 12px with a 2px vertical hit area, which is below any reasonable
    // target size and gave no hint of which view you were on.
    "inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs transition",
    isActive
      ? "bg-brand-100 font-medium text-brand-800 dark:bg-brand-900 dark:text-brand-100"
      : "text-ink-500 hover:bg-ink-100 hover:text-ink-800 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-200",
  );

/**
 * The workspace sidebar: switcher, primary navigation, the project list and a
 * plan footer.
 *
 * The project list is the reason the sidebar is worth its width — it is how
 * someone moves between the things they actually work on, so it is loaded with
 * the shell rather than behind a click.
 */
/**
 * Is the current URL inside this project?
 *
 * Compared segment by segment rather than with startsWith, so a project keyed
 * MP does not light up when the URL is for MPX.
 */
function useIsCurrentProject() {
  const { pathname } = useLocation();
  return (ref: string) => {
    const parts = pathname.split("/").filter(Boolean);
    const at = parts.indexOf("projects");
    return at !== -1 && parts[at + 1] === ref;
  };
}

export function Sidebar({
  membership,
  onNavigate,
}: {
  membership: MembershipSummary;
  onNavigate?: () => void;
}) {
  const isInProject = useIsCurrentProject();
  const [manuallyOpen, setManuallyOpen] = useState<Set<string>>(
    () => new Set(),
  );

  // Open when you are inside it, OR when you asked for it. Being in a project
  // should not cost a click to see its board, and opening another project's
  // backlog should not mean navigating there first just to find the link.
  const isExpanded = (project: Parameters<typeof projectRef>[0]) => {
    const ref = projectRef(project);
    return isInProject(ref) || manuallyOpen.has(ref);
  };
  const toggleExpanded = (ref: string) =>
    setManuallyOpen((current: Set<string>) => {
      const next = new Set(current);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  const workspace = useWorkspace();
  const projects = useProjects(workspace.slug, membership.id);
  const chatUnread = useChatUnreadTotal(workspace.slug);
  const notificationsUnread = useUnreadNotificationCount(workspace.slug);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
        <LogoMark />
        <span className="text-sm font-semibold tracking-tight">Taskly</span>
      </div>

      <div className="px-2.5 pb-2">
        <OrgSwitcher current={membership} />
      </div>

      <nav className="space-y-0.5 px-2.5" aria-label="Workspace">
        <NavLink
          to={workspace.path()}
          end
          className={linkClass}
          onClick={onNavigate}
        >
          <GlyphHome />
          Overview
        </NavLink>
        <NavLink
          to={workspace.path("/settings/members")}
          className={linkClass}
          onClick={onNavigate}
        >
          <GlyphPeople />
          {workspace.isPersonal ? "Members" : "Team"}
        </NavLink>
        <NavLink
          to={workspace.path("/chat")}
          className={linkClass}
          onClick={onNavigate}
        >
          <MessageCircle className="size-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">Chat</span>
          <UnreadPill count={chatUnread} label="unread messages" />
        </NavLink>
        <NavLink
          to={workspace.path("/notifications")}
          className={linkClass}
          onClick={onNavigate}
        >
          <Bell className="size-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">Notifications</span>
          <UnreadPill count={notificationsUnread.data ?? 0} label="unread notifications" />
        </NavLink>
        <NavLink
          to={workspace.path("/settings/labels")}
          className={linkClass}
          onClick={onNavigate}
        >
          <Tag className="size-4 shrink-0" aria-hidden="true" />
          Labels
        </NavLink>
        <NavLink
          to={workspace.path("/settings/billing")}
          className={linkClass}
          onClick={onNavigate}
        >
          <GlyphCard />
          Billing
        </NavLink>
      </nav>

      <div className="mt-6 min-h-0 flex-1 overflow-y-auto px-2.5 pb-4">
        <div className="flex items-center justify-between gap-2 px-2.5 pb-1.5">
          <p className="text-[11px] font-medium tracking-wide text-ink-400 uppercase">
            Projects
          </p>
          <NewProjectButton iconOnly />
        </div>

        {projects.isPending ? (
          <div className="space-y-1 px-2.5 py-1" aria-hidden="true">
            {[0, 1, 2].map((row) => (
              <div
                key={row}
                className="h-6 animate-pulse rounded bg-ink-100 dark:bg-ink-800"
              />
            ))}
          </div>
        ) : projects.isError ? (
          <p className={cn("px-2.5 text-xs", mutedTextClass)}>
            Projects could not be loaded.
          </p>
        ) : projects.data.length === 0 ? (
          // The dead end this replaces: the board, backlog and issue screens all
          // live under a project, so a workspace with none had nowhere to go.
          <div className="px-2.5">
            <p className={cn("text-xs", mutedTextClass)}>No projects yet.</p>
            <NewProjectButton size="sm" className="mt-2 w-full" />
          </div>
        ) : (
          <ul className="space-y-0.5">
            {projects.data.map((project) => (
              <li key={project.id}>
                <div className="flex items-center">
                  {/* A SIBLING of the link, not a child: a <button> inside an
                      <a> is invalid, and browsers disagree about which of the
                      two owns the click. */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(projectRef(project))}
                    aria-expanded={isExpanded(project)}
                    aria-label={`${isExpanded(project) ? "Collapse" : "Expand"} ${project.name}`}
                    className="-mr-0.5 flex size-5 shrink-0 items-center justify-center rounded text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 transition-transform",
                        isExpanded(project) && "rotate-90",
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  <NavLink
                    to={workspace.path(`/projects/${projectRef(project)}`)}
                    className={(state) =>
                      cn(linkClass(state), "min-w-0 flex-1")
                    }
                    onClick={onNavigate}
                  >
                    <ProjectIconTile
                      icon={project.icon ?? ""}
                      color={project.color_name ?? ""}
                      size="xs"
                      logoUrl={
                        project.has_logo
                          ? v2Api.projectLogoUrl(
                              workspace.slug,
                              projectRef(project),
                            )
                          : null
                      }
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {project.name}
                    </span>
                    {typeof project.tasks_count === "number" &&
                    project.tasks_count > 0 ? (
                      <span className="shrink-0 text-xs tabular-nums text-ink-400">
                        {project.tasks_count}
                      </span>
                    ) : null}
                  </NavLink>
                </div>
                {/* The sub-navigation belongs to the project you are IN.
                    Rendering it under every project put three controls on
                    screen for each one — nine rows of chrome for three
                    projects — none of which told you where you were. Now the
                    other projects are a single line each, and the open one
                    carries its own nav. */}
                {isExpanded(project) && (
                  <div className="mt-0.5 mb-1 ml-7 grid grid-cols-2 gap-1">
                    <NavLink
                      to={workspace.path(
                        `/projects/${projectRef(project)}/board`,
                      )}
                      className={subLinkClass}
                      onClick={onNavigate}
                      end
                    >
                      <Columns3
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      Board
                    </NavLink>
                    <NavLink
                      to={workspace.path(
                        `/projects/${projectRef(project)}/backlog`,
                      )}
                      className={subLinkClass}
                      onClick={onNavigate}
                      end
                    >
                      <ListTodo
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      Backlog
                    </NavLink>
                    <NavLink
                      to={workspace.path(
                        `/projects/${projectRef(project)}/reports`,
                      )}
                      className={subLinkClass}
                      onClick={onNavigate}
                      end
                    >
                      <BarChart3
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      Reports
                    </NavLink>
                    <NavLink
                      to={workspace.path(
                        `/projects/${projectRef(project)}/settings`,
                      )}
                      className={subLinkClass}
                      onClick={onNavigate}
                      aria-label={`${project.name} settings`}
                      title="Project settings"
                    >
                      <Settings
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      Settings
                    </NavLink>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isPaidPlan(membership.plan) ? (
        <div className="border-t border-ink-200 p-3 dark:border-ink-800">
          <p className="text-sm font-medium">Free plan</p>
          <p className={cn("mt-0.5 text-xs leading-relaxed", mutedTextClass)}>
            Unlock more seats, sprints, reports and unlimited projects.
          </p>
          <Link
            to={workspace.path("/settings/billing")}
            onClick={onNavigate}
            className="mt-2.5 flex h-8 w-full items-center justify-center rounded-lg bg-brand-600 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Upgrade
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function GlyphHome() {
  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true">
      <path
        d="M3.5 8.5 10 3.5l6.5 5v7a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GlyphPeople() {
  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true">
      <circle
        cx="8"
        cy="7"
        r="2.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3 16c0-2.5 2.2-4 5-4s5 1.5 5 4M14 6.2a2.6 2.6 0 0 1 0 5M15.5 15.8c0-1.6-.7-2.7-2-3.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GlyphCard() {
  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true">
      <rect
        x="2.75"
        y="5"
        width="14.5"
        height="10"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M2.75 8.5h14.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** A small count beside a nav item; renders nothing at zero. */
function UnreadPill({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={`${count} ${label}`}
      className="shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white tabular-nums"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
